import React, { useState, useMemo, useCallback } from "react";
import {
  Modal,
  TextInput,
  Text,
  Group,
  Box,
  Stack,
  ScrollArea,
  UnstyledButton,
  Divider,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import Fuse, { type IFuseOptions } from "fuse.js";

import { Car } from "@/utils/api";
import { AppButton, AppIcon } from "@/components/generic";
import { getCarDisplayName } from "@/utils/carHelpers";

// ============================================
// Types
// ============================================

interface CarOptionItem {
  id: string;
  label: string;
  details?: string;
  isAllOption?: boolean;
  car?: Car;
}

export interface CarPickerModalProps {
  opened: boolean;
  onClose: () => void;

  /** Currently selected car ID (null = "All Vehicles" if showAllOption) */
  value: string | null | undefined;

  /** Callback when a car is selected */
  onSelect: (carId: string | null, car?: Car) => void;

  /** Available cars to display */
  cars: Car[];

  /** Whether to show "All Vehicles" option */
  showAllOption?: boolean;

  /** Label for "All Vehicles" option */
  allOptionLabel?: string;

  /** Whether to show car details (make/model) */
  showDetails?: boolean;
}

// ============================================
// Constants
// ============================================

const ALL_VEHICLES_VALUE = "__all__";

const FUSE_OPTIONS: IFuseOptions<CarOptionItem> = {
  keys: [
    { name: "label", weight: 0.7 },
    { name: "details", weight: 0.3 },
  ],
  threshold: 0.35,
  distance: 100,
  minMatchCharLength: 2,
  ignoreLocation: true,
};

// ============================================
// Component
// ============================================

export const CarPickerModal: React.FC<CarPickerModalProps> = ({
  opened,
  onClose,
  value,
  onSelect,
  cars,
  showAllOption = false,
  allOptionLabel,
  showDetails = true,
}) => {
  const { t } = useTranslation(["refuels", "common"]);
  const [searchValue, setSearchValue] = useState("");

  // Reset search when modal opens
  React.useEffect(() => {
    if (opened) {
      setSearchValue("");
    }
  }, [opened]);

  const defaultAllOptionLabel =
    allOptionLabel || t("refuels:list.allVehicles");

  // Build option items
  const allOptions = useMemo((): CarOptionItem[] => {
    const options: CarOptionItem[] = [];

    if (showAllOption) {
      options.push({
        id: ALL_VEHICLES_VALUE,
        label: defaultAllOptionLabel,
        isAllOption: true,
      });
    }

    cars.forEach((car) => {
      if (car.id) {
        options.push({
          id: car.id,
          label: getCarDisplayName(car) || car.label || "Unnamed Vehicle",
          details: showDetails
            ? [car.vehicleMake?.makeName, car.model]
              .filter(Boolean)
              .join(" ") || undefined
            : undefined,
          car,
        });
      }
    });

    return options;
  }, [cars, showAllOption, defaultAllOptionLabel, showDetails]);

  // Fuse index for fuzzy search
  const fuse = useMemo(() => new Fuse(allOptions, FUSE_OPTIONS), [allOptions]);

  // Filtered options
  const filteredOptions = useMemo(() => {
    const search = searchValue.trim();
    if (!search) return allOptions;
    return fuse.search(search).map((result) => result.item);
  }, [allOptions, fuse, searchValue]);

  // Handle selection
  const handleSelect = useCallback(
    (option: CarOptionItem) => {
      if (option.isAllOption) {
        onSelect(null, undefined);
      } else {
        onSelect(option.id, option.car);
      }
      onClose();
    },
    [onSelect, onClose]
  );

  // Determine selected value for checkmark
  const selectedId = useMemo(() => {
    if (value === null && showAllOption) return ALL_VEHICLES_VALUE;
    return value ?? null;
  }, [value, showAllOption]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t("refuels:selectors.car.modalTitle", {
        defaultValue: "Select Vehicle",
      })}
      fullScreen
      zIndex={1000}
      styles={{
        body: {
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 60px)",
          padding: 0,
        },
      }}
    >
      {/* Search input */}
      <Box px="md" pt="xs" pb="sm" style={{ flexShrink: 0 }}>
        <TextInput
          placeholder={t("refuels:selectors.car.searchPlaceholder", {
            defaultValue: "Search vehicles...",
          })}
          value={searchValue}
          onChange={(e) => setSearchValue(e.currentTarget.value)}
          leftSection={<AppIcon iconName="Search" size={18} />}
          rightSection={
            searchValue ? (
              <AppButton
                iconName="X"
                iconSize={16}
                onClick={() => setSearchValue("")}
              />
            ) : undefined
          }
          rightSectionPointerEvents="auto"
          size="md"
          autoFocus
        />
      </Box>

      <Divider />

      {/* Car list */}
      <ScrollArea style={{ flex: 1 }} type="auto" offsetScrollbars>
        <Stack gap={0} px="md" py="xs">
          {filteredOptions.map((option) => {
            const isSelected = selectedId === option.id;

            return (
              <UnstyledButton
                key={option.id}
                onClick={() => handleSelect(option)}
                py="sm"
                px="xs"
                style={{ borderRadius: 8 }}
              >
                <Group gap="sm" wrap="nowrap" justify="space-between">
                  <Group gap="sm" wrap="nowrap" align="flex-start" style={{ flex: 1, minWidth: 0 }}>
                    <Box pt={2}>
                      <AppIcon iconName="Car" size={24} />
                    </Box>
                    <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                      <Text
                        size="md"
                        fw={option.isAllOption ? 500 : undefined}
                        lineClamp={1}
                      >
                        {option.label}
                      </Text>
                      {option.details && (
                        <Text size="sm" c="dimmed" lineClamp={1}>
                          {option.details}
                        </Text>
                      )}
                    </Stack>
                  </Group>
                  {isSelected && (
                    <AppIcon iconName="Check" size={18} color="blue" />
                  )}
                </Group>
              </UnstyledButton>
            );
          })}

          {/* No results */}
          {filteredOptions.length === 0 && searchValue.trim() && (
            <Text size="sm" c="dimmed" ta="center" py="lg">
              {t("common:form.noResults", {
                defaultValue: "No vehicles found",
              })}
            </Text>
          )}
        </Stack>
      </ScrollArea>
    </Modal>
  );
};